import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map } from 'lucide-react';
import type { ProcessBlueprint, GeneratedMapData } from '../../../../../types/processBlueprint';
import type { CardData, Connection } from '../../../../../types/card';
import { BlueprintStatusBadge } from '../../shared/BlueprintStatusBadge';
import { useProjectStore } from '../../../../../store/useProjectStore';
import { supabase } from '../../../../../lib/supabaseClient';

interface BlueprintOverviewTabProps {
  blueprint: ProcessBlueprint | null;
  onClose?: () => void;
}

export function BlueprintOverviewTab({ blueprint, onClose }: BlueprintOverviewTabProps) {
  const navigate = useNavigate();
  const addSheet = useProjectStore((s) => s.addSheet);
  const setCards = useProjectStore((s) => s.setCards);
  const setCurrentBlueprintId = useProjectStore((s) => s.setCurrentBlueprintId);
  const setCurrentProjectId = useProjectStore((s) => s.setCurrentProjectId);

  const handleOpenMap = useCallback(() => {
    if (!blueprint?.generatedMapData) return;
    const mapData = blueprint.generatedMapData;

    // Store blueprintId so MapaPage can find the template link
    if (blueprint?.id) {
      setCurrentBlueprintId(blueprint.id);
      supabase
        .from('process_blueprint_usage')
        .select('project_id')
        .eq('blueprint_id', blueprint.id)
        .limit(1)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.project_id) setCurrentProjectId(data.project_id);
        });
    }

    // Normalize connections: sourceId → Connection[]
    const connsBySrc = new window.Map<string, Connection[]>();
    for (const conn of mapData.connections) {
      if (!connsBySrc.has(conn.sourceId)) connsBySrc.set(conn.sourceId, []);
      connsBySrc.get(conn.sourceId)!.push({
        id: crypto.randomUUID(),
        targetId: conn.targetId,
        sourceHandle: 'right',
      });
    }

    const normalizedCards: CardData[] = mapData.cards.map((card) => ({
      id: card.id,
      tipo: card.tipo as CardData['tipo'],
      atividade: card.atividade || card.nome_fluxo || '',
      nome_fluxo: card.nome_fluxo,
      posicao: card.posicao ?? { x: 100, y: 200 },
      status: 'ativo' as const,
      tp: card.tp,
      lt: card.lt,
      tt: card.tt,
      traUnit: card.traUnit as CardData['traUnit'],
      entrada: card.entrada,
      sector: card.sector || card.setor,
      responsavel: card.responsavel || card.quem,
      pessoasSimultaneas: card.pessoasSimultaneas,
      resources: card.resources,
      quem: card.quem,
      conexoes_saida: connsBySrc.get(card.id) ?? [],
      ...(card.executionConfig ? { executionConfig: card.executionConfig } : {}),
    }));

    addSheet(blueprint.name);
    setCards(normalizedCards);
    onClose?.();
    navigate('/mapa');
  }, [blueprint, addSheet, setCards, navigate, onClose, setCurrentBlueprintId, setCurrentProjectId]);

  if (!blueprint) return <div className="text-sm text-gray-500">Nenhum blueprint carregado.</div>;

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-lg">{blueprint.name}</div>
        <BlueprintStatusBadge status={blueprint.status} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Info label="Categoria" value={blueprint.category || '-'} />
        <Info label="Versão" value={blueprint.versionSemver ?? '-'} />
        <Info label="Padrão" value={blueprint.isCorporateStandard ? 'Sim' : 'Não'} />
        <Info label="ID" value={blueprint.id} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Info label="Criado" value={new Date(blueprint.createdAt).toLocaleString()} />
        <Info label="Atualizado" value={new Date(blueprint.updatedAt).toLocaleString()} />
      </div>
      <div>
        <div className="text-gray-500">Descrição</div>
        <p className="leading-relaxed">{blueprint.description || 'Sem descrição'}</p>
      </div>

      {blueprint.generatedMapData && (
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-500 text-xs flex items-center gap-1">
              <Map size={12} /> Mapa do Processo
            </span>
            <button
              type="button"
              onClick={handleOpenMap}
              className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors dark:text-purple-300 dark:hover:text-purple-200"
            >
              Abrir no Editor
            </button>
          </div>
          <BlueprintMapPreview mapData={blueprint.generatedMapData} />
        </div>
      )}

      {!blueprint.generatedMapData && blueprint.sourceType === 'map' && blueprint.sourceId && (
        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs flex items-center gap-1">
              <Map size={12} /> Origem: Mapa VSM
            </span>
            <button
              type="button"
              onClick={() => {
                setCurrentProjectId(blueprint.sourceId!);
                navigate('/mapa');
                onClose?.();
              }}
              className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors dark:text-purple-300 dark:hover:text-purple-200"
            >
              Abrir Mapa Original
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BlueprintMapPreview({ mapData }: { mapData: GeneratedMapData }) {
  const activityCards = mapData.cards.filter((c) => c.tipo === 'atividade');

  const formatTp = (minutes: number) => {
    if (minutes >= 1440) return `${Math.round(minutes / 1440)}d`;
    if (minutes >= 60) return `${Math.round(minutes / 60)}h`;
    return `${minutes}min`;
  };

  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2 px-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center">
        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
      </div>
      {activityCards.map((card) => (
        <div key={card.id} className="flex items-center gap-1">
          <span className="text-gray-400 text-[10px]">\u2192</span>
          <div
            className="flex-shrink-0 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 text-[10px] leading-tight bg-white dark:bg-gray-800 max-w-[90px] truncate"
            title={card.atividade}
          >
            <span className="block truncate">{card.atividade}</span>
            {card.tp != null && (
              <span className="block text-gray-400 text-[9px]">{formatTp(card.tp)}</span>
            )}
          </div>
        </div>
      ))}
      <span className="text-gray-400 text-[10px]">\u2192</span>
      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
        <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-gray-500 text-xs">{label}</div>
      <div className="font-medium">{value ?? '-'}</div>
    </div>
  );
}
